/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import MENU_ROLES from '@/constants/ConstantMenuRoles';
import { NAMED_ROUTES } from '@/constants/ConstantTerceirosRotas';
import { useStoreAuth } from '@/stores/StoreAuth';
import { createRouter, createWebHistory, START_LOCATION } from 'vue-router';

export let wasPageReloaded = false;

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      component: () => import('@/layouts/LayoutAuth.vue'),
      children: [
        {
          path: '',
          name: 'RouteLogin',
          component: () => import('@/pages/auth/PageLogin.vue'),
        },
        {
          path: 'token/tfa/confirm/:token?',
          name: 'RouteConfirmTokenTfa',
          props: true,
          component: () => import('@/pages/auth/PageTfaToken.vue'),
        },
        {
          path: 'forgot/email',
          name: 'RouteForgotEmail',
          component: () => import('@/pages/auth/PageForgotPasswordEmail.vue'),
        },
        {
          path: 'forgot/email/confirm/:token',
          name: 'RouteForgotEmailConfirm',
          props: true,
          component: () => import('@/pages/auth/PageForgotPasswordConfirm.vue'),
        },
        {
          path: 'registration/first/access/:id',
          name: 'RouteTfaFirstAccess',
          props: true,
          component: () => import('@/pages/auth/PageTfaFirstAccess.vue'),
        },
      ],
    },
    {
      path: '/',
      component: () => import('@/layouts/LayoutMain.vue'),
      children: [
        {
          path: '',
          name: 'RouteHome',
          component: () => import('@/pages/PageHome.vue'),
        },
        {
          path: 'configuracoes',
          children: [
            {
              path: 'usuarios',
              name: 'RouteSettingsUsers',
              meta: {
                roles: MENU_ROLES.admin,
              },
              component: () => import('@/pages/management/PageManagementUsuarios.vue'),
            },
            {
              path: 'usuarios/:id',
              name: 'RouteSettingsUser',
              props: true,
              redirect: { name: 'RouteSettingsUserData' },
              component: () => import('@/pages/management/PageManagementUsuario.vue'),
              children: [
                {
                  path: 'dados',
                  name: 'RouteSettingsUserData',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioDados.vue'),
                },
                {
                  path: 'permissoes',
                  name: 'RouteSettingsUserPermissions',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioPermissoes.vue'),
                },
                {
                  path: 'redefinir-senha',
                  name: 'RouteSettingsUserSecurity',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioSeguranca.vue'),
                },
                {
                  path: 'autenticacao-2fa',
                  name: 'RouteSettingsUserAuthentication',
                  props: true,
                  meta: {
                    middleware: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioAuthentication.vue'),
                },
                {
                  path: 'estabelecimentos',
                  name: 'RouteSettingsUserEstablishments',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioEstabelecimentos.vue'),
                },
              ],
            },
            {
              path: 'empresas',
              children: [
                {
                  path: '',
                  name: 'RouteSettingsCompanies',
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/PageEmpresas.vue'),
                },
                {
                  path: 'areas',
                  name: 'RouteSettingsAreaOfExpertise',
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/PageAreaAtuacao.vue'),
                },
                {
                  path: 'estabelecimentos',
                  name: 'RouteSettingsEstablishments',
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/management/PageManagementEstabelecimentos.vue'),
                },
              ],
            },
            {
              path: 'e-social',
              children: [
                {
                  path: 'importacao',
                  name: 'RouteSettingsImport',
                  meta: {
                    roles: MENU_ROLES.admin,
                  },
                  component: () => import('@/pages/fonte-dados/PageFonteDadosUploadArquivoZip.vue'),
                },
              ],
            },
            {
              path: 'auditoria',
              name: 'RouteSettingsAudit',
              meta: {
                roles: MENU_ROLES.admin,
              },
              component: () => import('@/pages/management/PageManagementAuditoria.vue'),
            },
            {
              path: 'notificacoes',
              children: [
                {
                  path: 'agendamento-automatico',
                  name: 'RouteSettingsNotificationsAutomaticScheduling',
                  meta: {
                    roles: MENU_ROLES.notificacoesAgendamentoAutomatico,
                  },
                  component: () => import('@/pages/management/notificacoes/PageNotificacoesAgendamentoAutomatico.vue'),
                },
                {
                  path: '',
                  name: 'RouteSettingsNotifications',
                  meta: {
                    roles: MENU_ROLES.notificacoesListagem,
                  },
                  component: () => import('@/pages/management/notificacoes/PageNotificacoes.vue'),
                },
                {
                  path: 'categorias',
                  name: 'RouteSettingsNotificationsCategory',
                  meta: {
                    roles: MENU_ROLES.notificacoesCategoriasListagem,
                  },
                  component: () => import('@/pages/management/notificacoes/PageNotificacoesCategoria.vue'),
                },
                {
                  path: 'adicionar',
                  name: 'RouteSettingsNotificationsAdd',
                  meta: { roles: MENU_ROLES.notificacoesGerenciamento },
                  component: () => import('@/pages/management/notificacoes/PageNotificacoesAdicionar.vue'),
                },
                {
                  path: 'editar/:id',
                  name: 'RouteSettingsNotificationsEdit',
                  meta: { roles: MENU_ROLES.notificacoesGerenciamento },
                  component: () => import('@/pages/management/notificacoes/PageNotificacoesEditar.vue'),
                },
              ],
            },
          ],
        },
        {
          path: 'ativacao',
          name: 'RouteRegistrationUserActivation',
          props: true,
          meta: {
            roles: MENU_ROLES.admin,
          },
          component: () => import('@/pages/management/usuario/PageManagementUsuarioCadastradoAtivacao.vue'),
        },
        {
          path: 'central-de-ajuda',
          name: 'RouteCentralAjuda',
          component: () => import('@/pages/central-ajuda/PageCentralAjuda.vue'),
        },
        {
          path: 'minha-conta',
          name: 'RouteLayoutProfile',
          redirect: { name: 'RouteLayoutProfileEdit' },
          component: () => import('@/layouts/LayoutUserProfile.vue'),
          children: [
            {
              path: 'perfil',
              name: 'RouteLayoutProfileEdit',
              component: () => import('@/pages/auth/PagePerfil.vue'),
            },
            {
              path: 'editar-senha',
              name: 'RouteLayoutProfilePasswordChange',
              component: () => import('@/pages/auth/PageChangePassword.vue'),
            },
            {
              path: 'autenticacao-2fa',
              name: 'RouteLayoutProfileAuthentication',
              component: () => import('@/pages/auth/PageAuthentication2FA.vue'),
            },
          ],
        },
        {
          path: 'anexos',
          name: 'RouteDocumentsFile',
          component: () => import('@/pages/documents/files/PageFile.vue'),
        },
        {
          path: 'exportacoes',
          name: 'RouteDocumentsDownload',
          component: () => import('@/pages/documents/downloads/PageDownload.vue'),
        },
        {
          path: '/terceiros',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.listagem.inicio,
              meta: {
                roles: MENU_ROLES.terceirosListagem,
              },
              component: () => import('@/pages/terceiros/listagem/PaginaTerceirosListagem.vue'),
            },
            {
              path: 'novo',
              name: NAMED_ROUTES.terceiros.listagem.cadastro,
              meta: {
                roles: MENU_ROLES.terceirosListagemEdicao,
              },
              component: () => import('@/pages/terceiros/listagem/PaginaTerceirosCadastro.vue'),
            },
            {
              path: ':id/editar',
              name: NAMED_ROUTES.terceiros.listagem.editar,
              props: true,
              meta: {
                roles: MENU_ROLES.terceirosListagemEdicao,
              },
              component: () => import('@/pages/terceiros/listagem/PaginaTerceirosCadastro.vue'),
            },
          ],
        },
        {
          path: '/terceiros/encargos',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.encargos.inicio,
              meta: {
                roles: MENU_ROLES.terceirosEncargos,
              },
              component: () => import('@/pages/terceiros/encargos/PaginaTerceirosEncargos.vue'),
            },
            {
              path: 'codigo-recolhimento',
              name: NAMED_ROUTES.terceiros.encargos.codigoRecolhimento,
              meta: {
                roles: MENU_ROLES.terceirosEncargos,
              },
              component: () => import('@/pages/terceiros/encargos/PaginaTerceirosEncargosCodigoRecolhimento.vue'),
            },
          ],
        },
        {
          path: '/terceiros/folha-de-pagamento',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.folhaPagamento.inicio,
              meta: {
                roles: MENU_ROLES.terceirosFolhaPagamento,
              },
              component: () => import('@/pages/terceiros/folha-de-pagamento/PaginaTerceirosFolhaPagamento.vue'),
            },
          ],
        },
        {
          path: '/terceiros/monitoramento-documento',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.documentos.monitoramentoDeDocumentos,
              meta: {
                roles: MENU_ROLES.terceirosListagemMonitoramento,
              },
              component: () => import('@/pages/terceiros/documentos/PaginaTerceirosMonitoramentoDocumentos.vue'),
            },
          ],
        },
        {
          path: '/terceiros/controle-validade',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.documentos.controleValidade,
              meta: {
                roles: MENU_ROLES.terceirosListagemControleValidade,
              },
              component: () => import('@/pages/terceiros/documentos/PaginaTerceirosDocumentosControleValidade.vue'),
            },
          ],
        },
        {
          path: '/terceiros/tipo-documentos',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.documentos.tiposDocumentos,
              meta: {
                roles: MENU_ROLES.terceirosDocumentos,
              },
              component: () => import('@/pages/terceiros/documentos/PaginaTerceirosTipoDocumentos.vue'),
            },
            {
              path: 'linha-de-servico',
              name: NAMED_ROUTES.terceiros.documentos.linhaDeServico,
              meta: {
                roles: MENU_ROLES.terceirosDocumentos,
              },
              component: () => import('@/pages/terceiros/documentos/PaginaTerceirosLinhasDeServico.vue'),
            },
            {
              path: 'checklist/:documentoId/:documentoNome',
              name: NAMED_ROUTES.terceiros.documentos.checklist,
              props: true,
              meta: {
                roles: MENU_ROLES.terceirosDocumentos,
              },
              component: () => import('@/pages/terceiros/documentos/PaginaTerceirosTipoDocumentosChecklist.vue'),
            },
          ],
        },
        {
          path: '/terceiros/certidoes',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.certidoes.cadastro,
              meta: {
                roles: MENU_ROLES.terceirosListagemCertidoes,
              },
              component: () => import('@/pages/terceiros/certidoes/PaginaTerceirosCertidoes.vue'),
            },
            {
              path: 'historico/:tipo/:idModelo/:idTerceiro',
              name: NAMED_ROUTES.terceiros.certidoes.historico,
              meta: {
                roles: MENU_ROLES.terceirosListagemCertidoes,
              },
              component: () => import('@/pages/terceiros/certidoes/PaginaTerceirosCertidoesHistorico.vue'),
            },
          ],
        },
        {
          path: '/terceiros/solicitacoes',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.solicitacoes.inicio,
              meta: {
                roles: [
                  ...MENU_ROLES.terceirosSolicitacaoVisualizacao,
                  ...MENU_ROLES.terceirosSolicitacaoGerenciamento,
                ],
              },
              component: () => import('@/pages/terceiros/solicitacoes/PaginaTerceirosSolicitacoes.vue'),
            },
            {
              path: 'analise-documento/:id',
              name: NAMED_ROUTES.terceiros.solicitacoes.analiseDocumento,
              meta: {
                roles: [
                  ...MENU_ROLES.terceirosSolicitacaoVisualizacao,
                  ...MENU_ROLES.terceirosSolicitacaoGerenciamento,
                ],
              },
              component: () => import('@/pages/terceiros/solicitacoes/PaginaTerceirosAnaliseDocumento.vue'),
            },
            {
              path: 'analise-documento-tipo/:id/:tipoDocumentoId',
              name: NAMED_ROUTES.terceiros.solicitacoes.analiseDocumentoPorTipo,
              meta: {
                roles: [
                  ...MENU_ROLES.terceirosSolicitacaoVisualizacao,
                  ...MENU_ROLES.terceirosSolicitacaoGerenciamento,
                ],
              },
              component: () => import('@/pages/terceiros/solicitacoes/PaginaTerceirosAnaliseDocumentoPorTipo.vue'),
            },
            {
              path: 'analise-documento-tipo/:id/:tipoDocumentoId/trabalhador',
              name: NAMED_ROUTES.terceiros.solicitacoes.analiseDocumentoPorTipoTrabalhador,
              meta: {
                roles: [
                  ...MENU_ROLES.terceirosSolicitacaoVisualizacao,
                  ...MENU_ROLES.terceirosSolicitacaoGerenciamento,
                ],
              },
              component: () =>
                import('@/pages/terceiros/solicitacoes/PaginaTerceirosAnaliseDocumentoPorTrabalhador.vue'),
            },
          ],
        },
        {
          path: '/terceiros/trabalho-analogo',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.trabalhoAnalogo.inicio,
              meta: {
                roles: MENU_ROLES.terceirosListagemTrabalhoAnalogo,
              },
              component: () => import('@/pages/terceiros/trabalho-analogo/PaginaTerceirosTrabalhoAnalogo.vue'),
            },
            {
              path: 'empregadores/:id',
              name: NAMED_ROUTES.terceiros.trabalhoAnalogo.empregadores,
              meta: {
                roles: MENU_ROLES.terceirosListagemTrabalhoAnalogo,
              },
              component: () =>
                import('@/pages/terceiros/trabalho-analogo/PaginaTerceirosTrabalhoAnalogoEmpregadores.vue'),
            },
          ],
        },
        {
          path: '/terceiros/trabalhadores-alocados',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.trabalhadorAlocado.inicio,
              meta: {
                roles: MENU_ROLES.terceirosListagemTrabalharAlocado,
              },
              component: () => import('@/pages/terceiros/trabalhador-alocado/PaginaTerceirosTrabalhadorAlocado.vue'),
            },
            {
              path: ':id',
              name: NAMED_ROUTES.terceiros.trabalhadorAlocado.details,
              meta: {
                roles: MENU_ROLES.terceirosListagemTrabalharAlocado,
              },
              component: () =>
                import('@/pages/terceiros/trabalhador-alocado/PaginaTerceirosTrabalhadorAlocadoDetalhes.vue'),
            },
          ],
        },
        {
          path: '/terceiros/envio-de-documentos',
          children: [
            {
              path: '',
              name: NAMED_ROUTES.terceiros.envioDocumentos.inicio,
              meta: {
                roles: [
                  ...MENU_ROLES.terceirosEnvioDocumentosVisualizacao,
                  ...MENU_ROLES.terceirosEnvioDocumentoGerenciamento,
                ],
              },
              component: () => import('@/pages/terceiros/envio-de-documentos/PaginaTerceirosEnvioDocumentos.vue'),
            },
            {
              path: ':solicitacaoId/detalhes/:solicitacaoLinhaId',
              name: NAMED_ROUTES.terceiros.envioDocumentos.detalhes,
              props: true,
              meta: {
                roles: [
                  ...MENU_ROLES.terceirosEnvioDocumentosVisualizacao,
                  ...MENU_ROLES.terceirosEnvioDocumentoGerenciamento,
                ],
              },
              component: () =>
                import('@/pages/terceiros/envio-de-documentos/PaginaTerceirosEnvioDocumentosDetalhes.vue'),
            },
            {
              path: ':solicitacaoId/trabalhadores/:tipoDocumentoId/:solicitacaoLinhaId',
              name: NAMED_ROUTES.terceiros.envioDocumentos.trabalhadores,
              props: true,
              meta: {
                roles: [
                  ...MENU_ROLES.terceirosEnvioDocumentosVisualizacao,
                  ...MENU_ROLES.terceirosEnvioDocumentoGerenciamento,
                ],
              },
              component: () => import('@/pages/terceiros/envio-de-documentos/PaginaEnvioDocumentoTrabalhadores.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/playground',
      component: () => import('@/layouts/LayoutPlayground.vue'),
      children: [
        {
          path: '',
          name: 'StableHome',
          component: () => import('@/pages/playground/PagePlaygroundHome.vue'),
        },
        {
          path: 'Label',
          name: 'PlaygroundLabel',
          meta: { title: 'Labels', subtitle: 'AGLabel' },
          component: () => import('@/pages/playground/PagesPlaygroundLabel.vue'),
        },
        {
          path: 'button',
          name: 'PlaygroundButton',
          meta: { title: 'Buttons', subtitle: 'AGButton' },
          component: () => import('@/pages/playground/PagePlaygroundButtons.vue'),
        },
        {
          path: 'button-icon',
          name: 'PlaygroundButtonIcon',
          meta: { title: 'Icon Buttons', subtitle: 'AGButtonIcon' },
          component: () => import('@/pages/playground/PagePlaygroundButtonsIcon.vue'),
        },
        {
          path: 'expansion-panel',
          name: 'PlaygroundExpansionPanel',
          meta: { title: 'Expansion Panel', subtitle: 'AGExpansionPanel' },
          component: () => import('@/pages/playground/PagePlaygroundExpansionPanel.vue'),
        },
        {
          path: 'risco-legenda',
          name: 'PlaygroundRiscoLegenda',
          meta: { title: 'Risco Legenda', subtitle: 'AGRiscoLegenda' },
          component: () => import('@/pages/playground/PagePlaygroundRiscoLegenda.vue'),
        },
        {
          path: 'table-server',
          name: 'PlaygroundTableServer',
          meta: { title: 'Table Server', subtitle: 'AGDataTableServer' },
          component: () => import('@/pages/playground/PagePlaygroundTableServer.vue'),
        },
        {
          path: 'table',
          name: 'PlaygroundTable',
          meta: { title: 'Table', subtitle: 'AGDataTable' },
          component: () => import('@/pages/playground/PagePlaygroundTable.vue'),
        },
        {
          path: 'tabs',
          name: 'PlaygroundTabs',
          meta: { title: 'Tabs', subtitle: 'AGTabs' },
          component: () => import('@/pages/playground/PagePlaygroundTabs.vue'),
        },
        {
          path: 'tags',
          name: 'PlaygroundTags',
          meta: { title: 'Tags', subtitle: 'AGTag' },
          component: () => import('@/pages/playground/PagePlaygroundTags.vue'),
        },
        {
          path: 'switch',
          name: 'PlaygroundSwitch',
          meta: { title: 'Switches', subtitle: 'AGSwitch' },
          component: () => import('@/pages/playground/PagePlaygroundSwitch.vue'),
        },
        {
          path: 'menu',
          name: 'PlaygroundMenu',
          meta: { title: 'Menus', subtitle: 'AGMenu' },
          component: () => import('@/pages/playground/PagePlaygroundMenu.vue'),
        },
        {
          path: 'text-field',
          name: 'PlaygroundTextField',
          meta: { title: 'Text fields', subtitle: 'AGTextField' },
          component: () => import('@/pages/playground/PagePlaygroundTextField.vue'),
        },
        {
          path: 'text-area',
          name: 'PlaygroundTextArea',
          meta: { title: 'Text area', subtitle: 'AGTextArea' },
          component: () => import('@/pages/playground/PagePlaygroundTextArea.vue'),
        },
        {
          path: 'currency-input',
          name: 'PlaygroundCurrencyInput',
          meta: { title: 'Currency Inputs', subtitle: 'AGCurrencyInput' },
          component: () => import('@/pages/playground/PagePlaygroundCurrencyInput.vue'),
        },
        {
          path: 'autocomplete',
          name: 'PlaygroundAutocomplete',
          meta: { title: 'Autocompletes', subtitle: 'AGAutocomplete' },
          component: () => import('@/pages/playground/PagePlaygroundAutocomplete.vue'),
        },
        {
          path: 'filter',
          name: 'PlaygroundFilter',
          meta: { title: 'Filters', subtitle: 'AGFilter' },
          component: () => import('@/pages/playground/PagePlaygroundFilter.vue'),
        },
        {
          path: 'bar-graph',
          name: 'PlaygroundBarGraph',
          meta: { title: 'Gráfico de barras', subtitle: 'AGGraficoBarra' },
          component: () => import('@/pages/playground/PagePlaygroundGraficoBarra.vue'),
        },
        {
          path: 'icon',
          name: 'PlaygroundIcon',
          meta: { title: 'Icons', subtitle: 'AGIcon' },
          component: () => import('@/pages/playground/PagePlaygroundIcon.vue'),
        },
        {
          path: 'dialog',
          name: 'PlaygroundDialog',
          meta: { title: 'Dialogs', subtitle: 'AGDialog' },
          component: () => import('@/pages/playground/PagePlaygroundDialog.vue'),
        },
        {
          path: 'notification',
          name: 'PlaygroundNotification',
          meta: { title: 'Notifications', subtitle: 'AGNotification' },
          component: () => import('@/pages/playground/PagePlaygroundNotification.vue'),
        },
        {
          path: 'confirm',
          name: 'PlaygroundConfirm',
          meta: { title: 'Confirmações', subtitle: 'AGDialogConfirm' },
          component: () => import('@/pages/playground/PagePlaygroundConfirm.vue'),
        },
        {
          path: 'alert',
          name: 'PlaygroundAlert',
          meta: { title: 'Alerts', subtitle: 'AGAlert' },
          component: () => import('@/pages/playground/PagePlaygroundAlert.vue'),
        },
        {
          path: 'password',
          name: 'PlaygroundPassword',
          meta: { title: 'Password Field', subtitle: 'AGPasswordField' },
          component: () => import('@/pages/playground/PagePlaygroundPasswordField.vue'),
        },
        {
          path: 'input-file',
          name: 'PlaygroundInputFile',
          meta: { title: 'Input File', subtitle: 'AGInputFile' },
          component: () => import('@/pages/playground/PagePlaygroundInputFile.vue'),
        },
        {
          path: 'date-picker',
          name: 'PlaygroundDatePicker',
          meta: { title: 'Date Picker', subtitle: 'AGDatePicker' },
          component: () => import('@/pages/playground/PagePlaygroundDatePicker.vue'),
        },
        {
          path: 'date-picker-month',
          name: 'PlaygroundDatePickerMonth',
          meta: { title: 'Month Picker', subtitle: 'AGDatePickerMonth' },
          component: () => import('@/pages/playground/PagePlaygroundDatePickerMonth.vue'),
        },
        {
          path: 'select',
          name: 'PlaygroundSelect',
          meta: { title: 'Select', subtitle: 'AGSelect' },
          component: () => import('@/pages/playground/PagePlaygroundSelect.vue'),
        },
        {
          path: 'date-picker-range',
          name: 'PlaygroundDatePickerRange',
          meta: { title: 'Date Picker Range', subtitle: 'AGDatePickerRange' },
          component: () => import('@/pages/playground/PagePlaygroundDatePickerRange.vue'),
        },
        {
          path: 'month-picker-range',
          name: 'PlaygroundDatePickerRangeMonth',
          meta: { title: 'Month Picker Range', subtitle: 'AGDatePickerRangeMonth' },
          component: () => import('@/pages/playground/PagePlaygroundDatePickerRangeMonth.vue'),
        },
        {
          path: 'card',
          name: 'PlaygroundCard',
          meta: { title: 'Card', subtitle: 'AGCard' },
          component: () => import('@/pages/playground/PagePlaygroundCard.vue'),
        },
        {
          path: 'risco-matriz',
          name: 'PlaygroundRiscoMatriz',
          meta: { title: 'Matriz de Risco', subtitle: 'AGRiscoMatriz' },
          component: () => import('@/pages/playground/PagePlaygroundRiscoMatriz.vue'),
        },
        {
          path: 'message-color',
          name: 'RoutePlaygroundMessageColor',
          meta: { title: 'Message Color', subtitle: 'Estilizando os hints dos componentes' },
          component: () => import('@/pages/playground/PagePlaygroundMessageColor.vue'),
        },
        {
          path: 'radio-button',
          name: 'RoutePlaygroundRadioButton',
          meta: { title: 'Radio Button', subtitle: 'AGRadioButton' },
          component: () => import('@/pages/playground/PagePlaygroundRadioButton.vue'),
        },
      ],
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (!to.meta?.roles) return next();

  const storeAuth = useStoreAuth();

  if (!storeAuth.user?.id) {
    await storeAuth.getUser();
  }

  const hasPermission = to.meta.roles.some((role) => storeAuth.authorities?.includes(role));
  storeAuth.setMessage('');

  if (!storeAuth.token) {
    return next({ name: 'RouteLogin' });
  }

  if (!hasPermission) {
    storeAuth.setMessage(
      'Esta funcionalidade não está disponível no seu plano de serviço. Solicite este modulo para seu consultor da AGNES.',
    );

    return next({ name: 'RouteHome' });
  }

  wasPageReloaded = from === START_LOCATION;

  return next();
});

export default router;
